import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiAlertCircle, FiEdit, FiPlus, FiPower, FiSave, FiTrash2 } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DataTable } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { ChainBadge } from '@/components/common/ChainBadge'
import { useToast } from '@/hooks/useToast'
import { useRuntimeConfigCurrent, useSubscriptionPlans } from '@/hooks/useSystemConfigData'
import {
  useCreateOnchainProfile,
  useCreateOnchainPolicyPlanOverride,
  useDeleteOnchainPolicyPlanOverride,
  useOnchainPolicyPlanOverride,
  useOnchainPolicyPlanOverrides,
  useOnchainOperationPolicyCapabilities,
  useOnchainProfile,
  useOnchainProfiles,
  useRefreshOnchainPolicyCache,
  useSetOnchainPolicyPlanOverrideActive,
  useUpdateOnchainPolicyPlanOverride,
  useUpdateOnchainProfile,
} from '@/hooks/useAdminData'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  isBudgetSubsidyIncreaseUnavailable,
  isBudgetSubsidyReductionOnly,
  parseBudgetSubsidyInput,
  resolveSolanaBypassEnabledFromRuntimeConfig,
} from '@/utils/onchainOperationPolicy'
import type {
  CreateOnchainOperationProfilePayload,
  OnchainOperationPolicyCapability,
  OnchainOperationPolicyPlanOverride,
  OnchainOperationProfile,
  UpdateOnchainOperationPolicyPlanOverridePayload,
  UpdateOnchainOperationProfilePayload,
} from '@/types'

type FilterState = {
  chainId: string
  operationType: string
  planId: string
  isActive: 'all' | 'active' | 'inactive'
  limit: number
}

type DateTimeLabel = {
  date: string
  time: string
}

type BudgetGateBypassOverrideMode = 'inherit' | 'force_off' | 'force_on'

type PolicyRow = OnchainOperationProfile & {
  chainLabel: string
  updatedAtLabel: DateTimeLabel
}

type OverrideRow = OnchainOperationPolicyPlanOverride & {
  chainLabel: string
  planLabel: string
  updatedAtLabel: DateTimeLabel
}

type PolicyFormState = {
  isActive: boolean
  minNetUsd: string
  costWeight: string
  safetyFactor: string
  spentUsdMultiplier: string
  bypassBudgetGate: boolean
  kvTtlSeconds: string
  kvSampleSize: string
  bootstrapGasUnitsP90: string
  bootstrapTronEnergyP90: string
  bootstrapTronBandwidthP90: string
  sweepConfigJson: string
  solanaConfigJson: string
  tronTrxFallbackMaxTrx: string
  notes: string
}

type OverrideFormState = {
  planId: string
  chainId: string
  operationType: string
  isActive: boolean
  minNetUsdOverrideEnabled: boolean
  minNetUsd: string
  safetyFactorOverrideEnabled: boolean
  safetyFactor: string
  spentUsdMultiplierOverrideEnabled: boolean
  spentUsdMultiplier: string
  budgetSubsidyUsd: string
  budgetGateBypassMode: BudgetGateBypassOverrideMode
  notes: string
}

type PendingOverrideConfirmation = {
  kind: 'save' | 'activate'
  previousSubsidyUsd: number
  nextSubsidyUsd: number
  overrideId?: string
  payload?: UpdateOnchainOperationPolicyPlanOverridePayload
  isActive?: boolean
  expectedRevision?: number
}

const toDateTimeLabel = (iso: string): DateTimeLabel => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { date: iso, time: '' }
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString(),
  }
}

const UpdatedAtCell = ({ value }: { value: DateTimeLabel }) => (
  <VStack align="start" spacing={0} fontSize="2xs" lineHeight="short">
    <Text whiteSpace="nowrap">{value.date}</Text>
    <Text whiteSpace="nowrap">{value.time || '\u00a0'}</Text>
  </VStack>
)

const toNumberOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toIntegerOrUndefined = (value: string): number | undefined => {
  const n = toNumberOrUndefined(value)
  if (n === undefined) return undefined
  return Math.trunc(n)
}

const toJsonOrNull = (raw: string): unknown | null | undefined => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  try {
    return JSON.parse(trimmed) as unknown
  } catch {
    return undefined
  }
}

const toPrettyJson = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

const toBudgetGateBypassOverrideMode = (
  overrideEnabled: boolean | null | undefined,
  bypassEnabled: boolean | null | undefined
): BudgetGateBypassOverrideMode => {
  if (!overrideEnabled) return 'inherit'
  return bypassEnabled ? 'force_on' : 'force_off'
}

const toBudgetGateBypassModePayload = (
  mode: BudgetGateBypassOverrideMode
): Pick<
  UpdateOnchainOperationPolicyPlanOverridePayload,
  'bypassBudgetGateOverrideEnabled' | 'bypassBudgetGate'
> => {
  if (mode === 'inherit') {
    return {
      bypassBudgetGateOverrideEnabled: false,
      bypassBudgetGate: false,
    }
  }
  return {
    bypassBudgetGateOverrideEnabled: true,
    bypassBudgetGate: mode === 'force_on',
  }
}

const budgetGateBypassModeColor = (mode: BudgetGateBypassOverrideMode): string => {
  if (mode === 'force_on') return 'purple'
  if (mode === 'force_off') return 'orange'
  return 'gray'
}

const budgetGateBypassModeLabelKey = (mode: BudgetGateBypassOverrideMode): string => {
  if (mode === 'force_on') return 'systemConfig.onchainPolicy.overrides.bypassModes.forceOn'
  if (mode === 'force_off') return 'systemConfig.onchainPolicy.overrides.bypassModes.forceOff'
  return 'systemConfig.onchainPolicy.overrides.bypassModes.inherit'
}

const splitTwoLineLabel = (label: string): [string, string] => {
  const normalized = label.trim()
  if (!normalized) return ['-', '']

  const words = normalized.split(/\s+/)
  if (words.length > 1) {
    const splitIndex = Math.ceil(words.length / 2)
    return [words.slice(0, splitIndex).join(' '), words.slice(splitIndex).join(' ')]
  }

  const chars = Array.from(normalized)
  const splitIndex = Math.ceil(chars.length / 2)
  return [chars.slice(0, splitIndex).join(''), chars.slice(splitIndex).join('')]
}

const TwoLineLabel = ({
  label,
  align = 'start',
}: {
  label: string
  align?: 'start' | 'center'
}) => {
  const [firstLine, secondLine] = splitTwoLineLabel(label)

  return (
    <VStack as="span" align={align} spacing={0} lineHeight="shorter" whiteSpace="normal">
      <Text as="span">{firstLine}</Text>
      <Text as="span">{secondLine || '\u00a0'}</Text>
    </VStack>
  )
}

const mapPolicyToFormState = (policy: OnchainOperationProfile): PolicyFormState => ({
  isActive: policy.isActive ?? false,
  minNetUsd: policy.minNetUsd !== undefined && policy.minNetUsd !== null ? String(policy.minNetUsd) : '',
  costWeight: policy.costWeight !== undefined && policy.costWeight !== null ? String(policy.costWeight) : '',
  safetyFactor:
    policy.safetyFactor !== undefined && policy.safetyFactor !== null ? String(policy.safetyFactor) : '',
  spentUsdMultiplier:
    policy.spentUsdMultiplier !== undefined && policy.spentUsdMultiplier !== null
      ? String(policy.spentUsdMultiplier)
      : '',
  bypassBudgetGate: Boolean(policy.bypassBudgetGate),
  kvTtlSeconds:
    policy.kvTtlSeconds !== undefined && policy.kvTtlSeconds !== null ? String(policy.kvTtlSeconds) : '',
  kvSampleSize:
    policy.kvSampleSize !== undefined && policy.kvSampleSize !== null ? String(policy.kvSampleSize) : '',
  bootstrapGasUnitsP90:
    policy.bootstrapGasUnitsP90 !== undefined && policy.bootstrapGasUnitsP90 !== null
      ? String(policy.bootstrapGasUnitsP90)
      : '',
  bootstrapTronEnergyP90:
    policy.bootstrapTronEnergyP90 !== undefined && policy.bootstrapTronEnergyP90 !== null
      ? String(policy.bootstrapTronEnergyP90)
      : '',
  bootstrapTronBandwidthP90:
    policy.bootstrapTronBandwidthP90 !== undefined && policy.bootstrapTronBandwidthP90 !== null
      ? String(policy.bootstrapTronBandwidthP90)
      : '',
  sweepConfigJson: toPrettyJson(policy.sweepConfig),
  solanaConfigJson: toPrettyJson(policy.solanaConfig),
  tronTrxFallbackMaxTrx:
    policy.tronTrxFallbackMaxTrx !== undefined && policy.tronTrxFallbackMaxTrx !== null
      ? String(policy.tronTrxFallbackMaxTrx)
      : '',
  notes: policy.notes ?? '',
})

const createEmptyPolicyFormState = (): PolicyFormState => ({
  isActive: true,
  minNetUsd: '',
  costWeight: '',
  safetyFactor: '',
  spentUsdMultiplier: '',
  bypassBudgetGate: false,
  kvTtlSeconds: '',
  kvSampleSize: '',
  bootstrapGasUnitsP90: '',
  bootstrapTronEnergyP90: '',
  bootstrapTronBandwidthP90: '',
  sweepConfigJson: '',
  solanaConfigJson: '',
  tronTrxFallbackMaxTrx: '',
  notes: '',
})

const mapOverrideToFormState = (
  override: OnchainOperationPolicyPlanOverride
): OverrideFormState => ({
  planId: override.planId ?? '',
  chainId: override.chainId ?? '',
  operationType: override.operationType ?? '',
  isActive: override.isActive ?? false,
  minNetUsdOverrideEnabled: override.minNetUsdOverrideEnabled ?? false,
  minNetUsd: override.minNetUsd !== undefined && override.minNetUsd !== null ? String(override.minNetUsd) : '',
  safetyFactorOverrideEnabled: override.safetyFactorOverrideEnabled ?? false,
  safetyFactor:
    override.safetyFactor !== undefined && override.safetyFactor !== null ? String(override.safetyFactor) : '',
  spentUsdMultiplierOverrideEnabled: override.spentUsdMultiplierOverrideEnabled ?? false,
  spentUsdMultiplier:
    override.spentUsdMultiplier !== undefined && override.spentUsdMultiplier !== null
      ? String(override.spentUsdMultiplier)
      : '',
  budgetSubsidyUsd: Number.isFinite(override.budgetSubsidyUsd)
    ? override.budgetSubsidyUsd.toFixed(2)
    : '0.00',
  budgetGateBypassMode: toBudgetGateBypassOverrideMode(
    override.bypassBudgetGateOverrideEnabled,
    override.bypassBudgetGate
  ),
  notes: override.notes ?? '',
})

const createEmptyOverrideFormState = (): OverrideFormState => ({
  planId: '',
  chainId: '',
  operationType: '',
  isActive: true,
  minNetUsdOverrideEnabled: false,
  minNetUsd: '',
  safetyFactorOverrideEnabled: false,
  safetyFactor: '',
  spentUsdMultiplierOverrideEnabled: false,
  spentUsdMultiplier: '',
  budgetSubsidyUsd: '0.00',
  budgetGateBypassMode: 'inherit',
  notes: '',
})

export function OnchainOperationPolicyTab(props: {
  cardBg: string
  showBaselinePolicies?: boolean
  showPlanOverrides?: boolean
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const isMainnet = currentEnv === 'mainnet'
  const showBaselinePolicies = props.showBaselinePolicies ?? true
  const showPlanOverrides = props.showPlanOverrides ?? false
  const showMainnetOnlyNotice = showPlanOverrides && !showBaselinePolicies && !isMainnet

  const [filters, setFilters] = useState<FilterState>({
    chainId: '',
    operationType: '',
    planId: '',
    isActive: 'all',
    limit: 100,
  })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isOverrideOpen,
    onOpen: onOverrideOpen,
    onClose: onOverrideClose,
  } = useDisclosure()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creatingBaselineCapability, setCreatingBaselineCapability] =
    useState<OnchainOperationPolicyCapability | null>(null)
  const [editingOverrideId, setEditingOverrideId] = useState<string | null>(null)
  const [form, setForm] = useState<PolicyFormState | null>(null)
  const [overrideForm, setOverrideForm] = useState<OverrideFormState | null>(null)
  const [pendingOverrideConfirmation, setPendingOverrideConfirmation] =
    useState<PendingOverrideConfirmation | null>(null)
  const overrideConfirmationCancelRef = useRef<HTMLButtonElement | null>(null)

  const { data: policies, isFetching, refetch } = useOnchainProfiles(
    {
      chainId: filters.chainId || undefined,
      operationType: filters.operationType || undefined,
      isActive: filters.isActive === 'all' ? undefined : filters.isActive === 'active',
      limit: filters.limit,
    },
    { enabled: showBaselinePolicies || (showPlanOverrides && isMainnet) }
  )

  const { data: selectedPolicy, isLoading: isDetailLoading } = useOnchainProfile(
    editingId ?? undefined,
    { enabled: isOpen }
  )
  const { data: policyTargets } = useOnchainProfiles(
    { limit: 500 },
    { enabled: isMainnet && showPlanOverrides }
  )
  const { data: policyCapabilities, refetch: refetchCapabilities } = useOnchainOperationPolicyCapabilities({
    enabled: isMainnet && showPlanOverrides,
  })
  const {
    data: overrides,
    error: overridesError,
    isError: isOverridesError,
    isFetching: isOverridesFetching,
    refetch: refetchOverrides,
  } =
    useOnchainPolicyPlanOverrides({
      planId: filters.planId || undefined,
      chainId: filters.chainId || undefined,
      operationType: filters.operationType || undefined,
      isActive: filters.isActive === 'all' ? undefined : filters.isActive === 'active',
      limit: 200,
    }, { enabled: isMainnet && showPlanOverrides })
  const { data: selectedOverride, isLoading: isOverrideDetailLoading } = useOnchainPolicyPlanOverride(
    editingOverrideId ?? undefined,
    { enabled: isMainnet && showPlanOverrides && isOverrideOpen }
  )
  const updateMutation = useUpdateOnchainProfile()
  const createBaselineMutation = useCreateOnchainProfile()
  const createOverrideMutation = useCreateOnchainPolicyPlanOverride()
  const updateOverrideMutation = useUpdateOnchainPolicyPlanOverride()
  const setOverrideActiveMutation = useSetOnchainPolicyPlanOverrideActive()
  const deleteOverrideMutation = useDeleteOnchainPolicyPlanOverride()
  const refreshCacheMutation = useRefreshOnchainPolicyCache()
  const { data: runtimeConfig } = useRuntimeConfigCurrent({
    enabled: showBaselinePolicies || (showPlanOverrides && isMainnet),
  })
  const { data: subscriptionPlans, isFetching: isSubscriptionPlansFetching } = useSubscriptionPlans({
    enabled: isMainnet && showPlanOverrides,
  })

  const solanaBypassEnabled = useMemo(() => {
    return resolveSolanaBypassEnabledFromRuntimeConfig(runtimeConfig?.configJson)
  }, [runtimeConfig])

  const chainOptions = useMemo(() => {
    const items = policies ?? []
    const seen = new Map<string, string>()
    for (const p of items) {
      const label = p.chainCode ?? p.chainName ?? p.chainId
      if (p.chainId && !seen.has(p.chainId)) {
        seen.set(p.chainId, label)
      }
    }
    const options = Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }))
    return [{ value: '', label: t('common.all') }, ...options]
  }, [policies, t])

  const overrideFormChainOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const policy of policyTargets ?? []) {
      const label = policy.chainCode ?? policy.chainName ?? policy.chainId
      if (policy.chainId && !seen.has(policy.chainId)) {
        seen.set(policy.chainId, label)
      }
    }
    for (const capability of policyCapabilities ?? []) {
      const label = capability.chainCode || capability.chainId
      if (capability.chainId && !seen.has(capability.chainId)) {
        seen.set(capability.chainId, label)
      }
    }
    if (
      selectedOverride?.chainId
      && !seen.has(selectedOverride.chainId)
    ) {
      seen.set(
        selectedOverride.chainId,
        selectedOverride.chainCode ?? selectedOverride.chainName ?? selectedOverride.chainId
      )
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }))
  }, [policyCapabilities, policyTargets, selectedOverride])

  const overridePlanOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const item of overrides ?? []) {
      const label = item.planName ?? item.planId
      if (item.planId && !seen.has(item.planId)) {
        seen.set(item.planId, label)
      }
    }
    const options = Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }))
    return [{ value: '', label: t('common.all') }, ...options]
  }, [overrides, t])

  const overrideFormPlanOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const plan of subscriptionPlans ?? []) {
      if (plan.id && plan.name && !seen.has(plan.id)) {
        const label = plan.isActive
          ? plan.name
          : `${plan.name} (${t('common.inactive')})`
        seen.set(plan.id, label)
      }
    }
    if (
      selectedOverride?.planId
      && selectedOverride.planName
      && !seen.has(selectedOverride.planId)
    ) {
      seen.set(selectedOverride.planId, selectedOverride.planName)
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }))
  }, [selectedOverride, subscriptionPlans, t])

  const operationOptions = useMemo(() => {
    const items = policies ?? []
    const seen = new Set<string>()
    for (const p of items) {
      if (p.operationType) seen.add(p.operationType)
    }
    const options = Array.from(seen.values())
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({ value, label: value }))
    return [{ value: '', label: t('common.all') }, ...options]
  }, [policies, t])

  const overrideFormOperationOptions = useMemo(() => {
    const seen = new Set<string>()
    for (const policy of policyTargets ?? []) {
      if ((!overrideForm?.chainId || policy.chainId === overrideForm.chainId)
        && policy.operationType) {
        seen.add(policy.operationType)
      }
    }
    for (const capability of policyCapabilities ?? []) {
      if ((!overrideForm?.chainId || capability.chainId === overrideForm.chainId)
        && capability.operationType) {
        seen.add(capability.operationType)
      }
    }
    if (selectedOverride?.operationType) {
      seen.add(selectedOverride.operationType)
    }
    return Array.from(seen.values())
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({ value, label: value }))
  }, [overrideForm?.chainId, policyCapabilities, policyTargets, selectedOverride])

  const selectedOverrideCapability = useMemo(() => {
    if (!overrideForm?.chainId || !overrideForm.operationType) return null
    return (policyCapabilities ?? []).find(capability =>
      capability.chainId === overrideForm.chainId
      && capability.operationType === overrideForm.operationType
    ) ?? null
  }, [overrideForm?.chainId, overrideForm?.operationType, policyCapabilities])
  const storedOverrideSubsidyUsd = editingOverrideId
    && Number.isFinite(selectedOverride?.budgetSubsidyUsd)
    ? selectedOverride?.budgetSubsidyUsd ?? 0
    : 0
  const subsidyReductionOnly = isBudgetSubsidyReductionOnly(
    storedOverrideSubsidyUsd,
    selectedOverrideCapability?.planOverrideConfigurable ?? false
  )

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )

  const limitOptions = useMemo(
    () => [
      { value: '50', label: '50' },
      { value: '100', label: '100' },
      { value: '200', label: '200' },
    ],
    []
  )

  const rows = useMemo<PolicyRow[]>(() => {
    const items = policies ?? []
    return items.map(p => ({
      ...p,
      chainLabel: p.chainCode ?? p.chainName ?? p.chainId,
      updatedAtLabel: toDateTimeLabel(p.updatedAt),
    }))
  }, [policies])

  const overrideRows = useMemo<OverrideRow[]>(() => {
    const items = overrides ?? []
    return items.map(item => ({
      ...item,
      chainLabel: item.chainCode ?? item.chainName ?? item.chainId,
      planLabel: item.planName ?? item.planId,
      updatedAtLabel: toDateTimeLabel(item.updatedAt),
    }))
  }, [overrides])

  const handleEdit = useCallback(
    (id: string) => {
      setCreatingBaselineCapability(null)
      setEditingId(id)
      onOpen()
    },
    [onOpen]
  )

  const handleCreateBaseline = useCallback((capability: OnchainOperationPolicyCapability) => {
    onOverrideClose()
    setCreatingBaselineCapability(capability)
    setEditingId(null)
    setForm(createEmptyPolicyFormState())
    onOpen()
  }, [onOpen, onOverrideClose])

  const handleOverrideEdit = useCallback(
    (id: string) => {
      setEditingOverrideId(id)
      onOverrideOpen()
    },
    [onOverrideOpen]
  )

  const handleOverrideCreate = useCallback(() => {
    setEditingOverrideId(null)
    setOverrideForm(createEmptyOverrideFormState())
    onOverrideOpen()
  }, [onOverrideOpen])

  const persistOverride = useCallback(async (
    payload: UpdateOnchainOperationPolicyPlanOverridePayload,
    overrideId: string | null,
  ) => {
    try {
      if (overrideId) {
        await updateOverrideMutation.mutateAsync({ overrideId, data: payload })
      } else {
        await createOverrideMutation.mutateAsync(payload)
      }
      toast.success({ title: t('systemConfig.onchainPolicy.saveSuccess') })
      setPendingOverrideConfirmation(null)
      onOverrideClose()
      void refetchOverrides()
    } catch (error) {
      toast.error({
        title: t('systemConfig.onchainPolicy.saveFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }, [
    createOverrideMutation,
    onOverrideClose,
    refetchOverrides,
    t,
    toast,
    updateOverrideMutation,
  ])

  const handleOverrideSetActive = useCallback(
    async (row: OverrideRow, isActive: boolean) => {
      if (isActive && row.budgetSubsidyUsd > 0) {
        setPendingOverrideConfirmation({
          kind: 'activate',
          previousSubsidyUsd: row.budgetSubsidyUsd,
          nextSubsidyUsd: row.budgetSubsidyUsd,
          overrideId: row.id,
          isActive: true,
          expectedRevision: row.revision,
        })
        return
      }
      try {
        await setOverrideActiveMutation.mutateAsync({
          overrideId: row.id,
          isActive,
          expectedRevision: row.revision,
        })
        toast.success({ title: t('systemConfig.onchainPolicy.saveSuccess') })
      } catch (error) {
        toast.error({
          title: t('systemConfig.onchainPolicy.saveFailed'),
          description: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [setOverrideActiveMutation, t, toast]
  )

  const handleOverrideDelete = useCallback(
    async (row: OverrideRow) => {
      try {
        await deleteOverrideMutation.mutateAsync(row.id)
        toast.success({ title: t('common.deleted') })
      } catch (error) {
        toast.error({
          title: t('systemConfig.onchainPolicy.saveFailed'),
          description: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [deleteOverrideMutation, t, toast]
  )

  const columns = useMemo<ColumnConfig<PolicyRow>[]>(() => {
    return [
      {
        key: 'chainLabel',
        title: t('systemConfig.onchainPolicy.columns.chain'),
        width: '16%',
        render: (_, row) => (
          <ChainBadge
            chainId={row.chainCode ?? row.chainId}
            chainCode={row.chainCode}
            chainName={row.chainName ?? row.chainLabel}
            isTestnet={currentEnv === 'testnet'}
          />
        ),
        noWrap: true,
      },
      {
        key: 'operationType',
        title: t('systemConfig.onchainPolicy.columns.operation'),
        width: '16%',
        render: value => <Text noOfLines={1}>{value ?? '-'}</Text>,
        noWrap: true,
      },
      {
        key: 'minNetUsd',
        title: t('systemConfig.onchainPolicy.columns.minNetUsd'),
        width: '10%',
        render: value => <Text noOfLines={1}>{typeof value === 'number' ? value.toFixed(2) : '-'}</Text>,
        noWrap: true,
      },
      {
        key: 'costWeight',
        title: t('systemConfig.onchainPolicy.columns.costWeight'),
        width: '8%',
        render: value => <Text noOfLines={1}>{value ?? '-'}</Text>,
        noWrap: true,
      },
      {
        key: 'spentUsdMultiplier',
        title: (
          <TwoLineLabel
            label={t('systemConfig.onchainPolicy.columns.spentUsdMultiplier')}
          />
        ),
        width: '10%',
        render: value => <Text noOfLines={1}>{value ?? '-'}</Text>,
        noWrap: true,
      },
      {
        key: 'bypassBudgetGate',
        title: (
          <TwoLineLabel
            label={t('systemConfig.onchainPolicy.columns.bypassBudgetGate')}
            align="center"
          />
        ),
        width: '10%',
        render: (_, row) => (
          <Badge colorScheme={row.bypassBudgetGate ? 'purple' : 'gray'}>
            {row.bypassBudgetGate ? t('common.active') : t('common.inactive')}
          </Badge>
        ),
        align: 'center',
      },
      {
        key: 'isActive',
        title: t('systemConfig.onchainPolicy.columns.status'),
        width: '10%',
        render: (_, row) => (
          <Badge colorScheme={row.isActive ? 'green' : 'gray'}>
            {row.isActive ? t('common.active') : t('common.inactive')}
          </Badge>
        ),
        align: 'center',
      },
      {
        key: 'updatedAtLabel',
        title: t('systemConfig.onchainPolicy.columns.updatedAt'),
        width: '12%',
        render: (_, row) => <UpdatedAtCell value={row.updatedAtLabel} />,
      },
    ]
  }, [currentEnv, t])

  const actions = useMemo(() => {
    return (row: PolicyRow): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => handleEdit(row.id),
      },
    ]
  }, [handleEdit, t])

  const overrideColumns = useMemo<ColumnConfig<OverrideRow>[]>(() => {
    return [
      {
        key: 'planLabel',
        title: t('systemConfig.onchainPolicy.fields.plan'),
        width: '14%',
        render: (_, row) => <Text noOfLines={1}>{row.planLabel}</Text>,
        noWrap: true,
      },
      {
        key: 'chainLabel',
        title: t('systemConfig.onchainPolicy.columns.chain'),
        width: '14%',
        render: (_, row) => (
          <ChainBadge
            chainId={row.chainCode ?? row.chainId}
            chainCode={row.chainCode}
            chainName={row.chainName ?? row.chainLabel}
            isTestnet={currentEnv === 'testnet'}
          />
        ),
        noWrap: true,
      },
      {
        key: 'operationType',
        title: t('systemConfig.onchainPolicy.columns.operation'),
        width: '16%',
        render: value => <Text noOfLines={1}>{value ?? '-'}</Text>,
        noWrap: true,
      },
      {
        key: 'spentUsdMultiplier',
        title: t('systemConfig.onchainPolicy.columns.spentUsdMultiplier'),
        width: '11%',
        render: (_, row) => (
          <Text noOfLines={1}>
            {row.spentUsdMultiplierOverrideEnabled ? row.spentUsdMultiplier ?? '-' : '-'}
          </Text>
        ),
        noWrap: true,
      },
      {
        key: 'minNetUsd',
        title: t('systemConfig.onchainPolicy.columns.minNetUsd'),
        width: '11%',
        render: (_, row) => (
          <Text noOfLines={1}>
            {row.minNetUsdOverrideEnabled ? row.minNetUsd ?? '-' : '-'}
          </Text>
        ),
        noWrap: true,
      },
      {
        key: 'budgetSubsidyUsd',
        title: <TwoLineLabel label={t('systemConfig.onchainPolicy.columns.budgetSubsidyUsd')} />,
        width: '12%',
        render: (_, row) => <Text noOfLines={1}>{row.budgetSubsidyUsd.toFixed(2)}</Text>,
        noWrap: true,
      },
      {
        key: 'safetyFactor',
        title: <TwoLineLabel label={t('systemConfig.onchainPolicy.fields.safetyFactor')} />,
        width: '10%',
        render: (_, row) => (
          <Text noOfLines={1}>
            {row.safetyFactorOverrideEnabled ? row.safetyFactor ?? '-' : '-'}
          </Text>
        ),
        noWrap: true,
      },
      {
        key: 'bypassBudgetGate',
        title: (
          <TwoLineLabel
            label={t('systemConfig.onchainPolicy.columns.bypassBudgetGate')}
            align="center"
          />
        ),
        width: '10%',
        render: (_, row) => {
          const mode = toBudgetGateBypassOverrideMode(
            row.bypassBudgetGateOverrideEnabled,
            row.bypassBudgetGate
          )
          return (
            <Badge colorScheme={budgetGateBypassModeColor(mode)} px={2} py={1}>
              <TwoLineLabel
                label={t(budgetGateBypassModeLabelKey(mode))}
                align="center"
              />
            </Badge>
          )
        },
        align: 'center',
      },
      {
        key: 'isActive',
        title: t('systemConfig.onchainPolicy.columns.status'),
        width: '10%',
        render: (_, row) => (
          <Badge colorScheme={row.isActive ? 'green' : 'gray'}>
            {row.isActive ? t('common.active') : t('common.inactive')}
          </Badge>
        ),
        align: 'center',
      },
      {
        key: 'updatedAtLabel',
        title: t('systemConfig.onchainPolicy.columns.updatedAt'),
        width: '14%',
        render: (_, row) => <UpdatedAtCell value={row.updatedAtLabel} />,
      },
    ]
  }, [currentEnv, t])

  const overrideActions = useMemo(() => {
    return (row: OverrideRow): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => handleOverrideEdit(row.id),
      },
      {
        label: row.isActive ? t('common.disable') : t('common.enable'),
        icon: <FiPower />,
        colorScheme: row.isActive ? 'orange' : 'green',
        confirmMessage: row.isActive
          ? t('systemConfig.onchainPolicy.overrides.disableConfirm', {
              plan: row.planLabel,
              chain: row.chainLabel,
              operation: row.operationType,
            })
          : undefined,
        onClick: () => handleOverrideSetActive(row, !row.isActive),
      },
      {
        label: t('common.delete'),
        icon: <FiTrash2 />,
        colorScheme: 'red',
        confirmMessage: t('systemConfig.onchainPolicy.overrides.deleteConfirm'),
        onClick: () => handleOverrideDelete(row),
      },
    ]
  }, [
    handleOverrideDelete,
    handleOverrideEdit,
    handleOverrideSetActive,
    t,
  ])

  useEffect(() => {
    if (!isOpen) {
      setForm(null)
      setEditingId(null)
      setCreatingBaselineCapability(null)
      return
    }
    if (selectedPolicy) {
      setForm(mapPolicyToFormState(selectedPolicy))
    }
  }, [isOpen, selectedPolicy])

  useEffect(() => {
    if (!isOverrideOpen) {
      setOverrideForm(null)
      setEditingOverrideId(null)
      return
    }
    if (selectedOverride) {
      setOverrideForm(mapOverrideToFormState(selectedOverride))
    }
  }, [isOverrideOpen, selectedOverride])

  const updateField = useCallback(
    <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) => {
      setForm(prev => (prev ? { ...prev, [key]: value } : prev))
    },
    []
  )

  const updateOverrideField = useCallback(
    <K extends keyof OverrideFormState>(key: K, value: OverrideFormState[K]) => {
      setOverrideForm(prev => (prev ? { ...prev, [key]: value } : prev))
    },
    []
  )

  const handleSave = useCallback(async () => {
    if ((!editingId && !creatingBaselineCapability) || !form) return

    const minNetUsd = toNumberOrUndefined(form.minNetUsd)
    if (minNetUsd === undefined || minNetUsd < 0) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidMinNetUsd') })
      return
    }

    const sweepConfig = toJsonOrNull(form.sweepConfigJson)
    if (sweepConfig === undefined) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSweepJson') })
      return
    }

    const solanaConfig = toJsonOrNull(form.solanaConfigJson)
    if (solanaConfig === undefined) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSweepJson') })
      return
    }

    if (creatingBaselineCapability) {
      const safetyFactor = toNumberOrUndefined(form.safetyFactor)
      const spentUsdMultiplier = toNumberOrUndefined(form.spentUsdMultiplier)
      const kvTtlSeconds = toIntegerOrUndefined(form.kvTtlSeconds)
      const kvSampleSize = toIntegerOrUndefined(form.kvSampleSize)
      if (safetyFactor === undefined || safetyFactor < 1) {
        toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSafetyFactor') })
        return
      }
      if (spentUsdMultiplier === undefined || spentUsdMultiplier < 0) {
        toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSpentUsdMultiplier') })
        return
      }
      if (kvTtlSeconds === undefined || kvTtlSeconds < 60) {
        toast.error({
          title: t('systemConfig.onchainPolicy.errors.invalidKvTtlSeconds', {
            defaultValue: 'KV TTL must be an integer of at least 60 seconds',
          }),
        })
        return
      }
      if (kvSampleSize === undefined || kvSampleSize < 1 || kvSampleSize > 200) {
        toast.error({
          title: t('systemConfig.onchainPolicy.errors.invalidKvSampleSize', {
            defaultValue: 'KV sample size must be an integer between 1 and 200',
          }),
        })
        return
      }
    }

    const payload: UpdateOnchainOperationProfilePayload = {
      expectedRevision: selectedPolicy?.revision ?? 0,
      isActive: form.isActive,
      minNetUsd,
      costWeight: form.costWeight.trim() ? toNumberOrUndefined(form.costWeight) ?? null : null,
      safetyFactor: form.safetyFactor.trim() ? (toNumberOrUndefined(form.safetyFactor) as number) : undefined,
      spentUsdMultiplier: form.spentUsdMultiplier.trim()
        ? (toNumberOrUndefined(form.spentUsdMultiplier) as number)
        : undefined,
      bypassBudgetGate: form.bypassBudgetGate,
      kvTtlSeconds: form.kvTtlSeconds.trim() ? (toIntegerOrUndefined(form.kvTtlSeconds) as number) : undefined,
      kvSampleSize: form.kvSampleSize.trim() ? (toIntegerOrUndefined(form.kvSampleSize) as number) : undefined,
      bootstrapGasUnitsP90: form.bootstrapGasUnitsP90.trim()
        ? (toIntegerOrUndefined(form.bootstrapGasUnitsP90) as number)
        : null,
      bootstrapTronEnergyP90: form.bootstrapTronEnergyP90.trim()
        ? (toIntegerOrUndefined(form.bootstrapTronEnergyP90) as number)
        : null,
      bootstrapTronBandwidthP90: form.bootstrapTronBandwidthP90.trim()
        ? (toIntegerOrUndefined(form.bootstrapTronBandwidthP90) as number)
        : null,
      sweepConfig,
      solanaConfig,
      tronTrxFallbackMaxTrx: form.tronTrxFallbackMaxTrx.trim()
        ? (toNumberOrUndefined(form.tronTrxFallbackMaxTrx) as number)
        : null,
      notes: form.notes.trim() ? form.notes.trim() : null,
    }

    try {
      if (editingId) {
        await updateMutation.mutateAsync({ profileId: editingId, data: payload })
      } else if (creatingBaselineCapability) {
        const createPayload: CreateOnchainOperationProfilePayload = {
          ...payload,
          chainId: creatingBaselineCapability.chainId,
          operationType: creatingBaselineCapability.operationType,
        }
        await createBaselineMutation.mutateAsync(createPayload)
      }
      toast.success({ title: t('systemConfig.onchainPolicy.saveSuccess') })
      onClose()
      void refetch()
      void refetchCapabilities()
    } catch (error) {
      toast.error({
        title: t('systemConfig.onchainPolicy.saveFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }, [
    createBaselineMutation,
    creatingBaselineCapability,
    editingId,
    form,
    onClose,
    refetch,
    refetchCapabilities,
    selectedPolicy?.revision,
    t,
    toast,
    updateMutation,
  ])

  const handleOverrideSave = useCallback(async () => {
    if (!overrideForm) return
    if (!editingOverrideId) {
      if (!overrideForm.planId.trim() || !overrideForm.chainId.trim() || !overrideForm.operationType.trim()) {
        toast.error({ title: t('systemConfig.onchainPolicy.errors.requiredOverrideTarget') })
        return
      }
    }

    const minNetUsd = overrideForm.minNetUsd.trim() ? toNumberOrUndefined(overrideForm.minNetUsd) : null
    const safetyFactor = overrideForm.safetyFactor.trim() ? toNumberOrUndefined(overrideForm.safetyFactor) : null
    const spentUsdMultiplier = overrideForm.spentUsdMultiplier.trim()
      ? toNumberOrUndefined(overrideForm.spentUsdMultiplier)
      : null
    if (overrideForm.minNetUsdOverrideEnabled && (minNetUsd === undefined || minNetUsd === null || minNetUsd < 0)) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidMinNetUsd') })
      return
    }
    if (
      overrideForm.safetyFactorOverrideEnabled &&
      (safetyFactor === undefined || safetyFactor === null || safetyFactor < 1)
    ) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSafetyFactor') })
      return
    }
    if (
      overrideForm.spentUsdMultiplierOverrideEnabled &&
      (spentUsdMultiplier === undefined || spentUsdMultiplier === null || spentUsdMultiplier < 0)
    ) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidSpentUsdMultiplier') })
      return
    }
    const budgetSubsidy = parseBudgetSubsidyInput(overrideForm.budgetSubsidyUsd)
    if (!budgetSubsidy.valid) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.invalidBudgetSubsidyUsd') })
      return
    }
    const previousSubsidyUsd = editingOverrideId ? storedOverrideSubsidyUsd : 0
    if (isBudgetSubsidyIncreaseUnavailable({
      storedSubsidyUsd: previousSubsidyUsd,
      nextSubsidyUsd: budgetSubsidy.value,
      planOverrideConfigurable:
        selectedOverrideCapability?.planOverrideConfigurable ?? false,
    })) {
      toast.error({
        title: t('systemConfig.onchainPolicy.fields.budgetSubsidyUsdUnavailable'),
      })
      return
    }
    if (overrideForm.budgetGateBypassMode === 'force_on' && !overrideForm.notes.trim()) {
      toast.error({ title: t('systemConfig.onchainPolicy.errors.requiredBypassNotes') })
      return
    }
    const budgetGateBypassPayload = toBudgetGateBypassModePayload(overrideForm.budgetGateBypassMode)
    const payload: UpdateOnchainOperationPolicyPlanOverridePayload = {
      ...(!editingOverrideId
        ? {
            planId: overrideForm.planId.trim(),
            chainId: overrideForm.chainId.trim(),
            operationType: overrideForm.operationType.trim(),
          }
        : {}),
      isActive: overrideForm.isActive,
      minNetUsdOverrideEnabled: overrideForm.minNetUsdOverrideEnabled,
      minNetUsd,
      safetyFactorOverrideEnabled: overrideForm.safetyFactorOverrideEnabled,
      safetyFactor,
      spentUsdMultiplierOverrideEnabled: overrideForm.spentUsdMultiplierOverrideEnabled,
      spentUsdMultiplier,
      budgetSubsidyUsd: budgetSubsidy.value,
      ...budgetGateBypassPayload,
      notes: overrideForm.notes.trim() ? overrideForm.notes.trim() : null,
      ...(editingOverrideId
        ? { expectedRevision: selectedOverride?.revision ?? 0 }
        : {}),
    }

    const subsidyIncrease = budgetSubsidy.value > previousSubsidyUsd
    const subsidyEnable = Boolean(
      editingOverrideId
      && !selectedOverride?.isActive
      && overrideForm.isActive
      && budgetSubsidy.value > 0,
    )
    if (budgetSubsidy.value > 0 && (subsidyIncrease || subsidyEnable)) {
      setPendingOverrideConfirmation({
        kind: 'save',
        previousSubsidyUsd,
        nextSubsidyUsd: budgetSubsidy.value,
        overrideId: editingOverrideId ?? undefined,
        payload,
      })
      return
    }
    await persistOverride(payload, editingOverrideId)
  }, [
    editingOverrideId,
    overrideForm,
    persistOverride,
    selectedOverride?.revision,
    selectedOverride?.isActive,
    selectedOverrideCapability?.planOverrideConfigurable,
    storedOverrideSubsidyUsd,
    t,
    toast,
  ])

  const handleOverrideConfirmationClose = useCallback(() => {
    if (updateOverrideMutation.isPending || createOverrideMutation.isPending || setOverrideActiveMutation.isPending) {
      return
    }
    setPendingOverrideConfirmation(null)
  }, [
    createOverrideMutation.isPending,
    setOverrideActiveMutation.isPending,
    updateOverrideMutation.isPending,
  ])

  const handleOverrideConfirmationConfirm = useCallback(async () => {
    const pending = pendingOverrideConfirmation
    if (!pending) return
    if (pending.kind === 'save' && pending.payload) {
      await persistOverride(pending.payload, pending.overrideId ?? null)
      return
    }
    if (pending.kind === 'activate' && pending.overrideId) {
      try {
        await setOverrideActiveMutation.mutateAsync({
          overrideId: pending.overrideId,
          isActive: pending.isActive ?? true,
          expectedRevision: pending.expectedRevision ?? 0,
        })
        toast.success({ title: t('systemConfig.onchainPolicy.saveSuccess') })
        setPendingOverrideConfirmation(null)
      } catch (error) {
        toast.error({
          title: t('systemConfig.onchainPolicy.saveFailed'),
          description: error instanceof Error ? error.message : String(error),
        })
      }
    }
  }, [
    pendingOverrideConfirmation,
    persistOverride,
    setOverrideActiveMutation,
    t,
    toast,
  ])

  const handleRefreshCache = useCallback(async () => {
    try {
      await refreshCacheMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.actions.refreshCacheSuccess', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.actions.refreshCacheFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }, [refreshCacheMutation, t, toast])

  return (
    <VStack spacing={4} align="stretch">
      {showMainnetOnlyNotice ? (
        <MainnetOnlyNotice />
      ) : (
        <Card bg={props.cardBg}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="sm">{t('systemConfig.onchainPolicy.filtersTitle')}</Heading>
              <HStack spacing={2}>
                {showBaselinePolicies ? (
                  <RefreshIconButton
                    label={t('common.refresh')}
                    onClick={() => refetch()}
                    isLoading={isFetching}
                  />
                ) : null}
                {showBaselinePolicies ? (
                  <CacheRefreshIconButton
                    label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                    onClick={handleRefreshCache}
                    isLoading={refreshCacheMutation.isPending}
                  />
                ) : null}
              </HStack>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            {showBaselinePolicies ? (
              <HStack
                mb={4}
                justify={{ base: 'flex-start', md: 'space-between' }}
                align={{ base: 'stretch', md: 'center' }}
                flexWrap="wrap"
                gap={2}
              >
                <HStack
                  spacing={2}
                  flex={{ base: '1 1 100%', md: '0 1 auto' }}
                  flexWrap="wrap"
                  minW={0}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {t('systemConfig.onchainPolicy.solanaBypass')}
                  </Text>
                  <Badge colorScheme={solanaBypassEnabled ? 'green' : 'orange'}>
                    {solanaBypassEnabled ? t('common.active') : t('common.inactive')}
                  </Badge>
                </HStack>
                <Text
                  fontSize="xs"
                  color="gray.500"
                  flex={{ base: '1 1 100%', md: '1 1 auto' }}
                  minW={0}
                  overflowWrap="anywhere"
                >
                  SOLANA_ONCHAIN_POLICY_BYPASS_ENABLED
                </Text>
              </HStack>
            ) : null}
            <SimpleGrid columns={{ base: 1, md: 2, xl: showPlanOverrides ? 5 : 4 }} spacing={3}>
              {showPlanOverrides ? (
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>
                    {t('systemConfig.onchainPolicy.fields.plan')}
                  </FormLabel>
                  <DropdownSelect
                    value={filters.planId}
                    options={overridePlanOptions}
                    onChange={(value) => setFilters(prev => ({ ...prev, planId: value }))}
                    placeholder={t('common.all')}
                    size="xs"
                  />
                </FormControl>
              ) : null}
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('systemConfig.onchainPolicy.fields.chain')}</FormLabel>
                <DropdownSelect
                  value={filters.chainId}
                  options={chainOptions}
                  onChange={(value) => setFilters(prev => ({ ...prev, chainId: value }))}
                  placeholder={t('common.all')}
                  size="xs"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('systemConfig.onchainPolicy.fields.operation')}</FormLabel>
                <DropdownSelect
                  value={filters.operationType}
                  options={operationOptions}
                  onChange={(value) => setFilters(prev => ({ ...prev, operationType: value }))}
                  placeholder={t('common.all')}
                  size="xs"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('systemConfig.onchainPolicy.fields.status')}</FormLabel>
                <DropdownSelect
                  value={filters.isActive}
                  options={statusOptions}
                  onChange={(value) => {
                    setFilters(prev => ({ ...prev, isActive: value as FilterState['isActive'] }))
                  }}
                  size="xs"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('systemConfig.onchainPolicy.fields.limit')}</FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={limitOptions}
                  onChange={(value) => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {showBaselinePolicies ? (
        <Card bg={props.cardBg}>
          <CardHeader>
            <Heading size="md">{t('systemConfig.onchainPolicy.listTitle')}</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <DataTable columns={columns} data={rows} actions={actions} emptyMessage={t('common.noDataFound')} />
          </CardBody>
        </Card>
      ) : null}

      {showPlanOverrides && !showMainnetOnlyNotice ? (
        <Card bg={props.cardBg}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">{t('systemConfig.onchainPolicy.overrides.title')}</Heading>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FiPlus />}
                  onClick={handleOverrideCreate}
                  isDisabled={!isMainnet}
                >
                  {t('systemConfig.onchainPolicy.overrides.create')}
                </Button>
                <RefreshIconButton
                  label={t('common.refresh')}
                  onClick={() => refetchOverrides()}
                  isLoading={isOverridesFetching}
                  isDisabled={!isMainnet}
                />
              </HStack>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            {!isMainnet ? (
              <Text fontSize="sm" color="gray.500">
                {t('systemConfig.onchainPolicy.overrides.mainnetOnly')}
              </Text>
            ) : (
              <VStack spacing={4} align="stretch">
                {isOverridesError ? (
                  <Text fontSize="sm" color="red.500">
                    {t('systemConfig.onchainPolicy.overrides.loadFailed')}
                    {overridesError instanceof Error ? `: ${overridesError.message}` : ''}
                  </Text>
                ) : null}
                <DataTable
                  columns={overrideColumns}
                  data={overrideRows}
                  actions={overrideActions}
                  emptyMessage={t('common.noDataFound')}
                />
              </VStack>
            )}
          </CardBody>
        </Card>
      ) : null}

      {showBaselinePolicies || creatingBaselineCapability ? (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>
            {creatingBaselineCapability
              ? t('systemConfig.onchainPolicy.createTitle', { defaultValue: 'Create baseline policy' })
              : t('systemConfig.onchainPolicy.editTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">{t('systemConfig.onchainPolicy.fields.isActive')}</FormLabel>
                <Switch
                  isChecked={form?.isActive ?? false}
                  onChange={(e) => updateField('isActive', e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                <FormLabel mb="0" display="flex" alignItems="center" gap={1}>
                  {t('systemConfig.onchainPolicy.fields.bypassBudgetGate')}
                  <Tooltip
                    label={t('systemConfig.onchainPolicy.fields.bypassBudgetGateHelp')}
                    hasArrow
                    placement="top"
                  >
                    <Text
                      as="span"
                      aria-label={t('systemConfig.onchainPolicy.fields.bypassBudgetGateHelp')}
                      color="gray.500"
                      cursor="help"
                      display="inline-flex"
                      tabIndex={0}
                    >
                      <FiAlertCircle aria-hidden="true" />
                    </Text>
                  </Tooltip>
                </FormLabel>
                <Switch
                  isChecked={form?.bypassBudgetGate ?? false}
                  onChange={(e) => updateField('bypassBudgetGate', e.target.checked)}
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.minNetUsd')}</FormLabel>
                  <Input
                    value={form?.minNetUsd ?? ''}
                    onChange={(e) => updateField('minNetUsd', e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.costWeight')}</FormLabel>
                  <Input
                    value={form?.costWeight ?? ''}
                    onChange={(e) => updateField('costWeight', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.safetyFactor')}</FormLabel>
                  <Input
                    value={form?.safetyFactor ?? ''}
                    onChange={(e) => updateField('safetyFactor', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.spentUsdMultiplier')}</FormLabel>
                  <Input
                    value={form?.spentUsdMultiplier ?? ''}
                    onChange={(e) => updateField('spentUsdMultiplier', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.tronTrxFallbackMaxTrx')}</FormLabel>
                  <Input
                    value={form?.tronTrxFallbackMaxTrx ?? ''}
                    onChange={(e) => updateField('tronTrxFallbackMaxTrx', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.kvTtlSeconds')}</FormLabel>
                  <Input
                    value={form?.kvTtlSeconds ?? ''}
                    onChange={(e) => updateField('kvTtlSeconds', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.kvSampleSize')}</FormLabel>
                  <Input
                    value={form?.kvSampleSize ?? ''}
                    onChange={(e) => updateField('kvSampleSize', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.bootstrapGasUnitsP90')}</FormLabel>
                  <Input
                    value={form?.bootstrapGasUnitsP90 ?? ''}
                    onChange={(e) => updateField('bootstrapGasUnitsP90', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.bootstrapTronEnergyP90')}</FormLabel>
                  <Input
                    value={form?.bootstrapTronEnergyP90 ?? ''}
                    onChange={(e) => updateField('bootstrapTronEnergyP90', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.bootstrapTronBandwidthP90')}</FormLabel>
                  <Input
                    value={form?.bootstrapTronBandwidthP90 ?? ''}
                    onChange={(e) => updateField('bootstrapTronBandwidthP90', e.target.value)}
                    placeholder={t('common.optional')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.notes')}</FormLabel>
                  <Input value={form?.notes ?? ''} onChange={(e) => updateField('notes', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>{t('systemConfig.onchainPolicy.fields.sweepConfigJson')}</FormLabel>
                <Textarea
                  value={form?.sweepConfigJson ?? ''}
                  onChange={(e) => updateField('sweepConfigJson', e.target.value)}
                  minH="140px"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('systemConfig.onchainPolicy.fields.solanaConfigJson')}</FormLabel>
                <Textarea
                  value={form?.solanaConfigJson ?? ''}
                  onChange={(e) => updateField('solanaConfigJson', e.target.value)}
                  minH="140px"
                />
              </FormControl>

              {isDetailLoading ? <Text fontSize="sm">{t('common.loading')}</Text> : null}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<FiSave />}
                onClick={handleSave}
                isLoading={updateMutation.isPending || createBaselineMutation.isPending}
                isDisabled={!form || (!editingId && !creatingBaselineCapability)}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
        </Modal>
      ) : null}

      {showPlanOverrides && !showMainnetOnlyNotice ? (
        <Modal isOpen={isOverrideOpen} onClose={onOverrideClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>
            {editingOverrideId
              ? t('systemConfig.onchainPolicy.overrides.editTitle')
              : t('systemConfig.onchainPolicy.overrides.createTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.plan')}</FormLabel>
                  <DropdownSelect
                    value={overrideForm?.planId ?? ''}
                    options={overrideFormPlanOptions}
                    onChange={(value) => updateOverrideField('planId', value)}
                    placeholder={
                      isSubscriptionPlansFetching
                        ? t('common.loading')
                        : t('systemConfig.onchainPolicy.overrides.selectPlan')
                    }
                    isDisabled={Boolean(editingOverrideId) || isSubscriptionPlansFetching}
                    fontSize="xs"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.chain')}</FormLabel>
                  <DropdownSelect
                    value={overrideForm?.chainId ?? ''}
                    options={overrideFormChainOptions}
                    onChange={(value) => updateOverrideField('chainId', value)}
                    placeholder={t('systemConfig.onchainPolicy.overrides.selectChain')}
                    isDisabled={Boolean(editingOverrideId)}
                    fontSize="xs"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.onchainPolicy.fields.operation')}</FormLabel>
                  <DropdownSelect
                    value={overrideForm?.operationType ?? ''}
                    options={overrideFormOperationOptions}
                    onChange={(value) => updateOverrideField('operationType', value)}
                    placeholder={t('systemConfig.onchainPolicy.overrides.selectOperation')}
                    isDisabled={Boolean(editingOverrideId)}
                    fontSize="xs"
                  />
                </FormControl>
              </SimpleGrid>

              {selectedOverrideCapability?.legacyUnconfiguredAllowed
                && !selectedOverrideCapability.baselinePolicyId ? (
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm" color="orange.600" mb={2}>
                    {t('systemConfig.onchainPolicy.overrides.legacyUnconfigured', {
                      defaultValue: 'No baseline exists for this Solana operation. Create one before configuring a plan subsidy.',
                    })}
                  </Text>
                  <Button
                    size="sm"
                    leftIcon={<FiPlus />}
                    onClick={() => handleCreateBaseline(selectedOverrideCapability)}
                  >
                    {t('systemConfig.onchainPolicy.overrides.createBaseline', {
                      defaultValue: 'Create baseline policy',
                    })}
                  </Button>
                </Box>
              ) : null}

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">{t('systemConfig.onchainPolicy.fields.isActive')}</FormLabel>
                <Switch
                  isChecked={overrideForm?.isActive ?? false}
                  onChange={(e) => updateOverrideField('isActive', e.target.checked)}
                />
              </FormControl>

              <FormControl>
                <FormLabel mb="0" display="flex" alignItems="center" gap={1}>
                  {t('systemConfig.onchainPolicy.fields.budgetGateBypassMode')}
                  <Tooltip
                    label={t('systemConfig.onchainPolicy.fields.budgetGateBypassModeHelp')}
                    hasArrow
                    placement="top"
                  >
                    <Text
                      as="span"
                      aria-label={t('systemConfig.onchainPolicy.fields.budgetGateBypassModeHelp')}
                      color="gray.500"
                      cursor="help"
                      display="inline-flex"
                      tabIndex={0}
                    >
                      <FiAlertCircle aria-hidden="true" />
                    </Text>
                  </Tooltip>
                </FormLabel>
                <Box maxW={{ base: '100%', md: '360px' }}>
                  <DropdownSelect
                    value={overrideForm?.budgetGateBypassMode ?? 'inherit'}
                    options={[
                      {
                        value: 'inherit',
                        label: t('systemConfig.onchainPolicy.overrides.bypassModes.inherit'),
                      },
                      {
                        value: 'force_off',
                        label: t('systemConfig.onchainPolicy.overrides.bypassModes.forceOff'),
                      },
                      {
                        value: 'force_on',
                        label: t('systemConfig.onchainPolicy.overrides.bypassModes.forceOn'),
                      },
                    ]}
                    onChange={(value) =>
                      updateOverrideField(
                        'budgetGateBypassMode',
                        value as BudgetGateBypassOverrideMode
                      )
                    }
                    size="sm"
                    fontSize="sm"
                  />
                </Box>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0">{t('systemConfig.onchainPolicy.fields.minNetUsd')}</FormLabel>
                  <Switch
                    isChecked={overrideForm?.minNetUsdOverrideEnabled ?? false}
                    onChange={(e) => updateOverrideField('minNetUsdOverrideEnabled', e.target.checked)}
                  />
                </FormControl>
                <Input
                  value={overrideForm?.minNetUsd ?? ''}
                  onChange={(e) => updateOverrideField('minNetUsd', e.target.value)}
                  placeholder={t('common.optional')}
                />

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0">{t('systemConfig.onchainPolicy.fields.safetyFactor')}</FormLabel>
                  <Switch
                    isChecked={overrideForm?.safetyFactorOverrideEnabled ?? false}
                    onChange={(e) => updateOverrideField('safetyFactorOverrideEnabled', e.target.checked)}
                  />
                </FormControl>
                <Input
                  value={overrideForm?.safetyFactor ?? ''}
                  onChange={(e) => updateOverrideField('safetyFactor', e.target.value)}
                  placeholder={t('common.optional')}
                />

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0">{t('systemConfig.onchainPolicy.fields.spentUsdMultiplier')}</FormLabel>
                  <Switch
                    isChecked={overrideForm?.spentUsdMultiplierOverrideEnabled ?? false}
                    onChange={(e) => updateOverrideField('spentUsdMultiplierOverrideEnabled', e.target.checked)}
                  />
                </FormControl>
                <Input
                  value={overrideForm?.spentUsdMultiplier ?? ''}
                  onChange={(e) => updateOverrideField('spentUsdMultiplier', e.target.value)}
                  placeholder={t('common.optional')}
                />
                <FormControl
                  display="flex"
                  alignItems="center"
                  alignSelf="start"
                  minH="40px"
                >
                  <FormLabel mb="0">
                    {t('systemConfig.onchainPolicy.fields.budgetSubsidyUsd')}
                  </FormLabel>
                </FormControl>
                <Box>
                  <Input
                    value={overrideForm?.budgetSubsidyUsd ?? ''}
                    onChange={(e) => updateOverrideField('budgetSubsidyUsd', e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                    isDisabled={
                      !selectedOverrideCapability?.planOverrideConfigurable
                      && !subsidyReductionOnly
                    }
                  />
                  {!selectedOverrideCapability?.planOverrideConfigurable ? (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {subsidyReductionOnly
                        ? t('systemConfig.onchainPolicy.fields.budgetSubsidyUsdReductionOnly')
                        : t('systemConfig.onchainPolicy.fields.budgetSubsidyUsdUnavailable')}
                    </Text>
                  ) : null}
                </Box>
              </SimpleGrid>

              <FormControl isRequired={overrideForm?.budgetGateBypassMode === 'force_on'}>
                <FormLabel>{t('systemConfig.onchainPolicy.fields.notes')}</FormLabel>
                <Input
                  value={overrideForm?.notes ?? ''}
                  onChange={(e) => updateOverrideField('notes', e.target.value)}
                />
              </FormControl>

              {isOverrideDetailLoading ? <Text fontSize="sm">{t('common.loading')}</Text> : null}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onOverrideClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<FiSave />}
                onClick={handleOverrideSave}
                isLoading={updateOverrideMutation.isPending || createOverrideMutation.isPending}
                isDisabled={!overrideForm}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
        </Modal>
      ) : null}

      <AlertDialog
        isOpen={Boolean(pendingOverrideConfirmation)}
        leastDestructiveRef={overrideConfirmationCancelRef}
        onClose={handleOverrideConfirmationClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent className="system-config-dialog">
            <AlertDialogHeader>
              {t('systemConfig.onchainPolicy.overrides.subsidyConfirmationTitle', {
                defaultValue: 'Confirm platform-funded budget subsidy',
              })}
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                {t('systemConfig.onchainPolicy.overrides.subsidyConfirmationBody', {
                  defaultValue:
                    'This change lets the platform cover additional on-chain budget for the selected plan and operation.',
                })}
              </Text>
              {pendingOverrideConfirmation ? (
                <VStack align="stretch" spacing={1} mt={3} fontSize="sm">
                  <Text>
                    {t('systemConfig.onchainPolicy.overrides.subsidyConfirmationRange', {
                      defaultValue: 'Subsidy: ${{previous}} → ${{next}} USD',
                      previous: pendingOverrideConfirmation.previousSubsidyUsd.toFixed(2),
                      next: pendingOverrideConfirmation.nextSubsidyUsd.toFixed(2),
                    })}
                  </Text>
                  {pendingOverrideConfirmation.payload?.operationType ? (
                    <Text color="gray.500">
                      {pendingOverrideConfirmation.payload.operationType}
                    </Text>
                  ) : null}
                </VStack>
              ) : null}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={overrideConfirmationCancelRef}
                onClick={handleOverrideConfirmationClose}
                isDisabled={
                  updateOverrideMutation.isPending
                  || createOverrideMutation.isPending
                  || setOverrideActiveMutation.isPending
                }
              >
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="orange"
                ml={3}
                onClick={handleOverrideConfirmationConfirm}
                isLoading={
                  updateOverrideMutation.isPending
                  || createOverrideMutation.isPending
                  || setOverrideActiveMutation.isPending
                }
              >
                {t('common.confirm', { defaultValue: 'Confirm' })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  )
}
